import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError, formatMoney } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CreditCard, Search, ExternalLink, Download, Image as ImageIcon, X, FileSpreadsheet, FileText } from "lucide-react";
import DateRangeFilter from "@/components/DateRangeFilter";
import { exportToExcel, exportToPdf } from "@/lib/exportUtils";

export default function ResumenPagos() {
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [q, setQ] = useState("");
  const [data, setData] = useState({ items: [], count: 0, total: 0 });
  const [selected, setSelected] = useState({});
  const [viewImg, setViewImg] = useState(null);

  const load = async () => {
    try {
      const params = {};
      if (desde) params.desde = desde;
      if (hasta) params.hasta = hasta;
      const { data } = await api.get("/reports/resumen-pagos", { params });
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [desde, hasta]);

  const s = q.trim().toLowerCase();
  const filtered = s ? data.items.filter((it) =>
    (it.pedido || "").toLowerCase().includes(s) ||
    (it.factura || "").toLowerCase().includes(s) ||
    (it.cliente || "").toLowerCase().includes(s) ||
    (it.productos || "").toLowerCase().includes(s)
  ) : data.items;

  const selectedIds = Object.keys(selected).filter((k) => selected[k]);
  const allSelected = filtered.length > 0 && filtered.every((x) => selected[x.id]);
  const toggleAll = () => {
    if (allSelected) {
      const c = { ...selected };
      filtered.forEach((x) => delete c[x.id]);
      setSelected(c);
    } else {
      const c = { ...selected };
      filtered.forEach((x) => (c[x.id] = true));
      setSelected(c);
    }
  };

  const exportSel = () => {
    const rows = selectedIds.length > 0 ? filtered.filter((x) => selected[x.id]) : filtered;
    if (rows.length === 0) return toast.error("Nada para exportar");
    const csv = [
      ["Fecha", "Pedido", "Factura", "Cliente", "Productos", "Zona", "Monto", "Método", "Banco", "Referencia", "Vendedor", "Anticipo"],
      ...rows.map((r) => [
        r.fecha_pago, r.pedido, r.factura, r.cliente, r.productos, r.zona,
        r.monto, r.metodo_pago, r.banco, r.referencia, r.vendedor, r.es_anticipo ? "Sí" : "",
      ]),
    ]
      .map((row) => row.map((c) => {
        const s = String(c ?? "");
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      }).join(","))
      .join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `resumen_pagos_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
    toast.success(`Exportado (${rows.length} pagos)`);
  };

  const exportUniversal = (fmt) => {
    const rowsSrc = selectedIds.length > 0 ? filtered.filter((x) => selected[x.id]) : filtered;
    if (rowsSrc.length === 0) return toast.error("Nada para exportar");
    const columns = [
      { key: "fecha_pago", label: "Fecha", type: "date", width: 14 },
      { key: "pedido", label: "Pedido", width: 12 },
      { key: "factura", label: "Factura", width: 12 },
      { key: "cliente", label: "Cliente", width: 26 },
      { key: "productos", label: "Productos", width: 30 },
      { key: "zona", label: "Zona", width: 12 },
      { key: "monto", label: "Monto", type: "money", width: 14 },
      { key: "metodo_pago", label: "Método", width: 14 },
      { key: "banco", label: "Banco/Caja", width: 16 },
      { key: "referencia", label: "Referencia", width: 16 },
      { key: "vendedor", label: "Vendedor", width: 16 },
    ];
    const filters = { Desde: desde || "—", Hasta: hasta || "—", Búsqueda: q || "—" };
    const opts = { title: "Resumen de Pagos", filters, columns, rows: rowsSrc, filename: `resumen_pagos_${new Date().toISOString().slice(0, 10)}` };
    fmt === "xlsx" ? exportToExcel(opts) : exportToPdf({ ...opts, landscape: true });
  };

  return (
    <div className="space-y-6" data-testid="resumen-pagos-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500 font-semibold">Cobros</div>
          <h1 className="font-manrope font-black tracking-tight text-4xl mt-1 flex items-center gap-3">
            <CreditCard className="w-8 h-8 text-[#3B82F6]" /> Resumen de Pagos
          </h1>
          <p className="text-zinc-500 mt-2 text-sm">Todos los pagos y facturas con boleta/comprobante escaneado.</p>
        </div>
        <div className="flex gap-2 items-end flex-wrap">
          <DateRangeFilter desde={desde} hasta={hasta} setDesde={setDesde} setHasta={setHasta} testidPrefix="rp" />
          <Button onClick={exportSel} className="gap-2 bg-[#002FA7] hover:bg-[#00227A]" data-testid="rp-export-btn">
            <Download className="w-4 h-4" /> CSV {selectedIds.length > 0 ? `(${selectedIds.length})` : ""}
          </Button>
          <Button variant="outline" onClick={() => exportUniversal("xlsx")} className="gap-2" data-testid="rp-xlsx-btn">
            <FileSpreadsheet className="w-4 h-4" /> Excel
          </Button>
          <Button variant="outline" onClick={() => exportUniversal("pdf")} className="gap-2" data-testid="rp-pdf-btn">
            <FileText className="w-4 h-4" /> PDF
          </Button>
        </div>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Card className="p-4 border-zinc-200 rounded-md">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Pagos</div>
          <div className="font-manrope font-black text-3xl mt-1">{data.count}</div>
        </Card>
        <Card className="p-4 border-zinc-200 rounded-md">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Total cobrado</div>
          <div className="font-manrope font-black text-3xl mt-1 text-emerald-700">{formatMoney(data.total)}</div>
        </Card>
        <Card className="p-4 border-zinc-200 rounded-md">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Con boleta</div>
          <div className="font-manrope font-black text-3xl mt-1">{data.items.filter((x) => x.tiene_boleta).length}</div>
        </Card>
      </div>

      <div className="relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar pedido, factura, cliente o producto..." className="pl-9" data-testid="rp-search" />
      </div>

      <Card className="border-zinc-200 rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-zinc-600">
              <tr className="text-left">
                <th className="px-3 py-3 w-8">
                  <input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="rp-select-all" />
                </th>
                <th className="px-3 py-3 text-xs uppercase tracking-widest font-semibold">Fecha</th>
                <th className="px-3 py-3 text-xs uppercase tracking-widest font-semibold">Pedido</th>
                <th className="px-3 py-3 text-xs uppercase tracking-widest font-semibold">Factura</th>
                <th className="px-3 py-3 text-xs uppercase tracking-widest font-semibold">Cliente</th>
                <th className="px-3 py-3 text-xs uppercase tracking-widest font-semibold">Productos</th>
                <th className="px-3 py-3 text-xs uppercase tracking-widest font-semibold">Zona</th>
                <th className="px-3 py-3 text-xs uppercase tracking-widest font-semibold text-right">Monto</th>
                <th className="px-3 py-3 text-xs uppercase tracking-widest font-semibold">Método</th>
                <th className="px-3 py-3 text-xs uppercase tracking-widest font-semibold">Boleta</th>
                <th className="px-3 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {filtered.length === 0 && (
                <tr><td colSpan={11} className="text-center text-zinc-400 py-10">
                  <CreditCard className="w-8 h-8 mx-auto mb-2 text-zinc-300" />
                  Sin pagos registrados en el rango.
                </td></tr>
              )}
              {filtered.map((it) => (
                <tr key={it.id} className={`hover:bg-zinc-50 ${selected[it.id] ? "bg-blue-50/60" : ""}`} data-testid={`rp-row-${it.id}`}>
                  <td className="px-3 py-3">
                    <input type="checkbox" checked={!!selected[it.id]} onChange={() => setSelected((p) => ({ ...p, [it.id]: !p[it.id] }))} data-testid={`rp-select-${it.factura}`} />
                  </td>
                  <td className="px-3 py-3 text-xs font-mono">{it.fecha_pago}</td>
                  <td className="px-3 py-3">
                    <Link to={`/pedidos/${it.pedido_id}`} className="font-mono text-xs font-semibold text-[#002FA7] hover:underline">{it.pedido}</Link>
                  </td>
                  <td className="px-3 py-3 font-mono text-xs">
                    {it.factura}
                    {it.es_anticipo && <span className="ml-1 px-1 py-0.5 bg-blue-100 text-blue-800 rounded text-[9px] uppercase">Ant.</span>}
                  </td>
                  <td className="px-3 py-3 text-xs">{it.cliente}</td>
                  <td className="px-3 py-3 text-xs text-zinc-700 max-w-xs truncate" title={it.productos}>{it.productos}</td>
                  <td className="px-3 py-3 text-xs text-zinc-600 max-w-[140px] truncate" title={it.zona}>{it.zona || "—"}</td>
                  <td className="px-3 py-3 text-right tabular-nums font-semibold text-emerald-700">{formatMoney(it.monto)}</td>
                  <td className="px-3 py-3 text-xs">
                    <div>{it.metodo_pago || "—"}</div>
                    {it.banco && <div className="text-[10px] text-zinc-500">{it.banco}</div>}
                    {it.referencia && <div className="text-[10px] text-zinc-500 font-mono">{it.referencia}</div>}
                  </td>
                  <td className="px-3 py-3">
                    {it.tiene_boleta ? (
                      <button
                        onClick={() => setViewImg(it.boleta_base64)}
                        className="text-blue-700 hover:underline text-xs inline-flex items-center gap-1"
                        data-testid={`rp-boleta-${it.factura}`}
                      >
                        <ImageIcon className="w-3 h-3" /> Ver
                      </button>
                    ) : <span className="text-zinc-300 text-xs">—</span>}
                  </td>
                  <td className="px-3 py-3 text-right">
                    <Link to={`/pedidos/${it.pedido_id}`} className="text-xs text-[#002FA7] hover:underline">
                      <ExternalLink className="w-3 h-3 inline" />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {viewImg && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setViewImg(null)}>
          <div className="relative max-w-2xl max-h-[90vh]">
            <button onClick={() => setViewImg(null)} className="absolute -top-8 right-0 text-white p-1"><X className="w-6 h-6" /></button>
            <img src={viewImg} alt="boleta" className="max-w-full max-h-[85vh] object-contain rounded bg-white" />
          </div>
        </div>
      )}
    </div>
  );
}
